<template>
    <v-dialog
        v-if="report && Object.keys(report).length > 0"
        v-model="show"
        max-width="500px"
        persistent
    >
        <v-snackbar
            v-model="showAlert"
            color="red lighten-1"
            text
            top
            right
            rounded
            class="mt-3"
        >
            {{ alertMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn
                    color="red lighten-1"
                    text
                    v-bind="attrs"
                    @click="showToast = false"
                >
                    <v-icon color="red lighten-1">
                        close
                    </v-icon>
                </v-btn>
            </template>
        </v-snackbar>
        <v-card>
            <v-card-title class="text-h6">
                Generate Report
            </v-card-title>
            <v-divider />
            <v-card-text
                v-if="!reportGenerateStatus"
                class="mt-4"
            >
                <h3>{{ report.title }}</h3>
                <span class="">{{ report.description }}</span>
                <v-form
                    ref="form"
                    v-model="valid"
                    lazy-validation
                    class="mt-4"
                >
                    <!-- 'date_field'-->
                    <v-select
                        v-if="report.param_csv.includes('date_field')"
                        v-model="date_field"
                        :items="dateFields"
                        item-text="title"
                        item-value="value"
                        outlined
                        label="Date Field"
                    />
                    <!-- 'accounts'-->
                    <v-select
                        v-if="report.param_csv.includes('accounts')"
                        v-model="accounts"
                        :items="companyUsers"
                        item-text="value"
                        item-value="account"
                        outlined
                        multiple
                        label="User(s)*"
                    >
                        <template v-slot:prepend-item>
                            <v-list-item
                                ripple
                                @mousedown.prevent
                                @click="selectAllAccounts"
                            >
                                <v-list-item-action>
                                    <v-icon :color="accounts.length > 0 ? 'indigo darken-4' : ''">
                                        {{ selectAccountIcon }}
                                    </v-icon>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title>
                                        Select All
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider class="mt-2" />
                        </template>
                        <template #selection="{ item, index }">
                            <v-chip
                                v-if="index === 0 && accounts.length === companyUsers.length"
                                small
                            >
                                All Users
                            </v-chip>
                            <v-chip
                                v-if="accounts.length !== companyUsers.length"
                                small
                            >
                                {{ item.value }}
                            </v-chip>
                        </template>
                    </v-select>
                    <!-- 'member_codes'-->
                    <v-select
                        v-if="report.param_csv.includes('member_codes')"
                        v-model="member_codes"
                        :items="companyMemberCodes"
                        item-text="member_code"
                        item-value="member_code"
                        outlined
                        multiple
                        label="Member Code(s)*"
                    >
                        <template v-slot:prepend-item>
                            <v-list-item
                                ripple
                                @mousedown.prevent
                                @click="selectAllMemberCodes"
                            >
                                <v-list-item-action>
                                    <v-icon :color="member_codes.length > 0 ? 'indigo darken-4' : ''">
                                        {{ selectMemberCodeIcon }}
                                    </v-icon>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title>
                                        Select All
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider class="mt-2" />
                        </template>
                        <template #selection="{ item, index }">
                            <v-chip

                                v-if="index === 0 && member_codes.length === companyMemberCodes.length"
                                small
                            >
                                All Member Codes
                            </v-chip>
                            <v-chip
                                v-if="member_codes.length !== companyMemberCodes.length"
                                small
                            >
                                {{ item.member_code }}
                            </v-chip>
                        </template>
                    </v-select>
                    <!-- 'response_codes'-->
                    <v-select
                        v-if="report.param_csv.includes('response_codes')"
                        v-model="response_codes"
                        :items="companyResponseCodes"
                        item-text="response_code"
                        item-value="response_code"
                        outlined
                        multiple
                        label="Response Code(s)*"
                    >
                        <template v-slot:prepend-item>
                            <v-list-item
                                ripple
                                @mousedown.prevent
                                @click="selectAllResponseCodes"
                            >
                                <v-list-item-action>
                                    <v-icon :color="response_codes.length > 0 ? 'indigo darken-4' : ''">
                                        {{ selectResponseCodeIcon }}
                                    </v-icon>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title>
                                        Select All
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider class="mt-2" />
                        </template>
                        <template #selection="{ item, index }">
                            <v-chip
                                v-if="index === 0 && response_codes.length === companyResponseCodes.length"
                                small
                            >
                                All Response Codes
                            </v-chip>
                            <v-chip
                                v-if="response_codes.length !== companyResponseCodes.length"
                                small
                            >
                                {{ item.response_code }}
                            </v-chip>
                        </template>
                    </v-select>
                    <!-- 'billing_codes'-->
                    <v-select
                        v-if="report.param_csv.includes('billing_codes')"
                        v-model="billing_codes"
                        :items="companyBillingCodes"
                        item-value="code"
                        item-text="codeEnabled"
                        outlined
                        multiple
                        label="Billing Code(s)*"
                    >
                        <template v-slot:prepend-item>
                            <v-list-item
                                ripple
                                @mousedown.prevent
                                @click="selectAllBillingCodes"
                            >
                                <v-list-item-action>
                                    <v-icon :color="billing_codes.length > 0 ? 'indigo darken-4' : ''">
                                        {{ selectBillingCodeIcon }}
                                    </v-icon>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title>
                                        Select All
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider class="mt-2" />
                        </template>
                        <template #selection="{ item, index }">
                            <v-chip
                                v-if="index === 0 && billing_codes.length === companyBillingCodes.length"
                                small
                            >
                                All Billing Codes
                            </v-chip>
                            <v-chip
                                v-if="billing_codes.length !== companyBillingCodes.length"
                                small
                            >
                                {{ item.code }}
                            </v-chip>
                        </template>
                    </v-select>
                    <!-- 'locators'-->
                    <v-select
                        v-if="report.param_csv.includes('locators')"
                        v-model="locators"
                        :items="companyLocators"
                        item-text="title"
                        item-value="uid"
                        outlined
                        multiple
                        label="Locator(s)*"
                    >
                        <template v-slot:prepend-item>
                            <v-list-item
                                ripple
                                @mousedown.prevent
                                @click="selectAllLocators"
                            >
                                <v-list-item-action>
                                    <v-icon :color="locators.length > 0 ? 'indigo darken-4' : ''">
                                        {{ selectLocatorsIcon }}
                                    </v-icon>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title>
                                        Select All
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider class="mt-2" />
                        </template>
                        <template #selection="{ item, index }">
                            <v-chip
                                v-if="index === 0 && locators.length === companyLocators.length"
                                small
                            >
                                All Locators
                            </v-chip>
                            <v-chip
                                v-if="locators.length !== companyLocators.length"
                                small
                            >
                                {{ item.title }}
                            </v-chip>
                        </template>
                    </v-select>
                    <!-- 'date_from' and 'date_to'-->
                    <v-menu
                        v-if="report.param_csv.includes('date_from') && report.param_csv.includes('date_to')"
                        ref="dateMenu"
                        v-model="dateMenu"
                        :close-on-content-click="false"
                        :return-value.sync="date"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="dateText"
                                label="Date Range*"
                                append-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                outlined
                                v-on="on"
                            />
                        </template>
                        <v-date-picker
                            v-model="date"
                            no-title
                            range
                            scrollable
                        >
                            <v-spacer />
                            <v-btn
                                text
                                color="primary"
                                @click="dateMenu = false"
                            >
                                Cancel
                            </v-btn>
                            <v-btn
                                text
                                color="primary"
                                @click="selectDate(date)"
                            >
                                OK
                            </v-btn>
                        </v-date-picker>
                    </v-menu>
                    <!-- 'date_day'-->
                    <v-menu
                        v-if="report.param_csv.includes('date_day')"
                        ref="date_picker"
                        v-model="date_picker"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="dateDayText"
                                label="Date"
                                append-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                outlined
                                v-on="on"
                            />
                        </template>
                        <v-date-picker
                            v-model="date_day"
                            no-title
                            @input="date_picker = false"
                        />
                    </v-menu>                   
                    <!-- 'orderBy'-->
                    <v-select
                        v-if="report.orderby_csv && report.orderby_csv.length > 0"                    
                        v-model="orderBy"
                        :items="orderByParam"
                        item-text="label"
                        item-value="value"
                        outlined
                        label="Order By"
                    />
                    <!-- 'format'-->
                    <v-select                     
                        v-model="format"
                        :items="formats"
                        item-text="label"
                        item-value="value"
                        outlined
                        label="Format"
                    />
                </v-form>
            </v-card-text>
            <v-card-text
                v-if="reportGenerateStatus === 'PROCESSING'"
                class="mt-4 pb-8 text-center"
            >
                <v-progress-circular
                    :size="60"
                    color="primary"
                    indeterminate
                />
                <span class="subtitle-2 d-block">Please wait while your report is being generated ...</span>
            </v-card-text>
            <v-card-text
                v-if="reportGenerateStatus === 'PROCESSED'"
                class="mt-4 pb-6 text-center"
            >
                <v-icon
                    large
                    color="success"
                >
                    mdi-checkbox-marked-circle
                </v-icon>
                <span class="subtitle-2 d-block my-2">Completed successfully</span>
                <v-row class="text-left download-file mt-3">
                    <v-col cols="1">
                        <v-icon class="file-icon">
                            {{ reportIcon }}
                        </v-icon>
                    </v-col>
                    <v-col
                        cols="9"
                        class="subtitle-1 font-weight-medium"
                    >
                        {{ reportFileName }}
                    </v-col>
                    <v-col cols="2">
                        <v-btn
                            small
                            class="float-right primary px-0 mt-1"
                            style="text-transform: none !important; height: 32px;"
                            :href="reportLink"
                            target="_blank"
                        >
                            Open
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-text
                v-if="reportGenerateStatus === 'FAILED'"
                class="mt-6 pb-8 text-center"
            >
                <span class="subtitle-2 d-block red--text">{{ reportGenerateError }}</span>
            </v-card-text>
            <v-card-actions class="px-6 pb-4 pt-0">
                <v-spacer />
                <v-btn
                    v-if="reportGenerateStatus === '' || reportGenerateStatus === 'PROCESSING'"
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    outlined
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="closeDialog"
                >
                    Cancel
                </v-btn>
                <v-btn
                    v-else-if="reportGenerateStatus === 'PROCESSED'"
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    outlined
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="closeDialog"
                >
                    Close
                </v-btn>
                <v-btn
                    v-else-if="reportGenerateStatus === 'FAILED'"
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    outlined
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="reportGenerateStatus = ''"
                >
                    Close
                </v-btn>
                <v-btn
                    v-if="!reportGenerateStatus"
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    depressed
                    :disabled="!formValid"
                    color="primary"
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="generateReport"
                >
                    Generate
                </v-btn>
                <v-spacer v-if="this.$vuetify.breakpoint.xs" />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
import webtmsBackend from '@/services/webtmsBackend';
import constants from '@/constants';

import dayjs from 'dayjs';
import 'dayjs/plugin/utc'; // import UTC plugin
import 'dayjs/plugin/advancedFormat'; // import advancedFormat plugin

export default {
    props: {
        value: Boolean,
        report: {
            type: Object,
            required: true,
            default: () => {}
        }
    },
    data: () => ({
        valid: false,
        date_field: 'due_date',        
        accounts: [],
        member_codes: [],       
        response_codes: [],
        billing_codes: [],
        locators: [],        
        dateMenu: null,
        date_to: dayjs().endOf('month').format('YYYY-MM-DD'),
        date_from: dayjs().startOf('month').format('YYYY-MM-DD'),        
        date_day: dayjs().startOf('month').format('YYYY-MM-DD'),
        orderBy: '',       
        format:'',
        cid: '',        
        formValid: false,
        showAlert: false,
        date_picker: false,
        alertMessage: '',
        reportGenerateStatus: '',
        reportRowId: '',
        reportFileName: '',
        reportGenerateError: '',        
        dateFields: [
            { title: 'Due Date', value: 'due_date' },
            { title: 'Work Date', value: 'work_date' },
            { title: 'Expires Date', value: 'expires_date' }
        ],
        date: [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')],
    }),    
    computed: {
        user() {
            return this.$store.getters.user;
        },
        formats () {
            const reportFormats = this.report.format_csv.split(','); 
            return reportFormats.map(item => {
                return {
                    label: item.toUpperCase(),
                    value: item.toLowerCase()
                };
            });
        },
        params() {
            return [this.report.param_csv];           
        },
        requiredParams() {
            if(this.report.param_csv === '') {
                return [];
            }
            const params = this.report.param_csv.split(',');
            return params;
        },
        orderByParam() {
            const orderBy = this.report.orderby_csv.split(',');
            if(orderBy.length === 0) {
                return [];
            }
            return orderBy.map(item => {
                return {
                    label: item.toUpperCase(),
                    value: item.toLowerCase()
                };
            });      
        },
        show: {
            get() { 
                console.log('computed.show.get', this.value);
                if(this.value === true) {
                    this.resetFieldsToDefault();
                }
                return this.value;
            },
            set(value) {
                console.log('computed.show.set', value);
                console.log('computed.show.set', this.report);
                this.$emit('input', value);
                this.resetFieldsToDefault();
            },
        },
        companyUsers() {
            // build list of users for this company, including deleted users and excluding SYSOP accounts (if any)
            let companyUsersData = this.$store.getters.companyUsers;
            let companyUsersDeletedData = this.$store.getters.companyUsersDeleted;
            companyUsersDeletedData.map(item => {
                item.deleted = true;
                return item;
            });
            let allUsers = [{account:'SYSTEM', name:'(Auto-Responses)'}];
            allUsers = allUsers.concat(companyUsersData).concat(companyUsersDeletedData);
            allUsers = allUsers.filter(user => {
                return user.ingroup !== 'SYSOP';
            });
            allUsers.map(item => {
                if(item.deleted) {
                    item.value = `${item.account} "${item.name}" (deleted)`;
                } else {
                    item.value = `${item.account} "${item.name}"`;
                }
                return item;
            });
            return allUsers;
        },
        selectedAllAccounts () {
            return this.accounts.length === this.companyUsers.length;
        },
        selectedSomeAccounts () {
            return this.accounts.length > 0 && !this.selectedAllAccounts;
        },
        selectAccountIcon () {
            if (this.selectedAllAccounts) return 'mdi-close-box';
            if (this.selectedSomeAccounts) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },
        companyMemberCodes() {
            return this.$store.getters.companyMemberCodes;
        },
        selectedAllMemberCodes () {
            return this.member_codes.length === this.companyMemberCodes.length;
        },
        selectedSomeMemberCodes () {
            return this.member_codes.length > 0 && !this.selectedAllMemberCodes;
        },
        selectMemberCodeIcon () {
            if (this.selectedAllMemberCodes) return 'mdi-close-box';
            if (this.selectedSomeMemberCodes) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },
        companyResponseCodes () {
            return this.$store.getters.companyResponseCodes;
        },
        selectedAllResponseCodes () {
            return this.response_codes.length === this.companyResponseCodes.length;
        },
        selectedSomeResponseCodes () {
            return this.response_codes.length > 0 && !this.selectedAllResponseCodes;
        },
        selectResponseCodeIcon () {
            if (this.selectedAllResponseCodes) return 'mdi-close-box';
            if (this.selectedSomeResponseCodes) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },
        companyBillingCodes () {
            let codes = [...this.$store.getters.companyBilling.codes];
            codes.forEach((item) => { item.codeEnabled = item.code + (item.enabled ? '':' (disabled)'); });            
            return codes;
        },
        selectedAllBillingCodes () {
            return this.billing_codes.length === this.companyBillingCodes.length;
        },
        selectedSomeBillingCodes () {
            return this.billing_codes.length > 0 && !this.selectedAllBillingCodes;
        },
        selectBillingCodeIcon () {
            if (this.selectedAllBillingCodes) return 'mdi-close-box';
            if (this.selectedSomeBillingCodes) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },
        dateText () {
            return this.date.map(dt => dayjs(dt).format('MM/DD/YYYY')).join(' ~ ');
        },
        reportLink() {
            return `${constants.NEWTIN_DOMAIN || ''}/NewtinWeb/userdata/${this.$store.getters.user.account}$${this.$store.getters.user.uid.slice(1, -1)}/${this.reportFileName}`;
        },
        reportIcon() {
            const ext = this.reportFileName.split('.').pop().toUpperCase();
            if (ext === 'PDF') {
                return 'mdi-file-pdf-box';
            } else if(ext === 'DOC' || ext === 'DOCX') {
                return 'mdi-file-word';
            } else if(ext === 'XLS' || ext === 'XLSX') {
                return 'mdi-file-excel';
            } else if(ext === 'TIFF') {
                return 'mdi-file-image';
            } else {
                return 'mdi-file';
            }
        },
        // Locators
        companyLocators() {
            let overviewAssigned = [...this.$store.getters.overviewOpenTickets.overviewAssigned];
            overviewAssigned.unshift({ account: '',  name: 'un-assigned', uid: '00000000-0000-0000-0000-000000000000' });
            return overviewAssigned.map((item) => {
                item.title = item.account + ' (' + item.name +')'; 
                return item;
            });
        },
        selectedAllLocators () {
            return this.locators.length === this.companyLocators.length;
        },
        selectedSomeLocators () {
            return this.locators.length > 0 && !this.selectedAllLocators;
        },
        selectLocatorsIcon () {
            if (this.selectedAllLocators) return 'mdi-close-box';
            if (this.selectedSomeLocators) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },
        dateDayText () {
            return this.date_day ? dayjs(this.date_day).format('MM/DD/YYYY') : dayjs().format('MM/DD/YYYY');
        },
    },
    watch: {
        format() {
            this.validateForm();
        },
        orderBy() {
            this.validateForm();
        },
        accounts() {
            this.validateForm();
        },
        member_codes() {
            this.validateForm();
        },
        response_codes() {
            this.validateForm();
        },
        date() {
            this.validateForm();    
        },
        locators() {
            this.validateForm();
        },
        date_field() {
            this.validateForm();
        },
        date_day() {
            this.validateForm();
        }
    },
    mounted() {
        this.resetFieldsToDefault();       
    },
    methods: {
        resetFieldsToDefault() {
            this.formValid = false;
            if(this.report) {
                this.accounts = this.companyUsers.map(item => item.account);
                this.member_codes = this.companyMemberCodes.map(item => item.member_code);
                this.response_codes = this.companyResponseCodes.map(item => item.response_code);
                this.billing_codes = this.companyBillingCodes.map(item => item.code);
                this.locators = this.companyLocators.map(item => item.uid);
                this.reportGenerateStatus = '';
                this.reportRowId = '';
                this.reportFileName = '';
                this.date_field = 'due_date';
                this.date_picker = false;
                this.date_day = dayjs().startOf('month').format('YYYY-MM-DD');
                if(this.report.name.startsWith('TMS/')) {
                    this.date = [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().add(1, 'months').startOf('month').format('YYYY-MM-DD')];
                } else {
                    this.date = [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')];
                }
                this.setOrderBy();
                this.setFormat();           

            }           
        },
        setOrderBy() {
            const orderBy = this.report.orderby_csv.split(',');
            if (Array.isArray(orderBy) && orderBy.length > 0) {
                this.orderBy = orderBy[0];
            }           
        },
        setFormat() {
            const reportFormats = this.report.format_csv.split(','); 
            if (Array.isArray(reportFormats) && reportFormats.length > 0) {
                this.format = reportFormats[0].toLowerCase();
            }
        },
        selectAllAccounts() {
            this.$nextTick(() => {
                if (this.selectedAllAccounts) {
                    this.accounts = [];
                } else {
                    const users = [...this.companyUsers];
                    this.accounts = users.map(item => item.account);
                }
            });
        },
        selectAllMemberCodes() {
            this.$nextTick(() => {
                if (this.selectedAllMemberCodes) {
                    this.member_codes = [];
                } else {
                    const mbrs = [...this.companyMemberCodes];
                    this.member_codes = mbrs.map(item => item.member_code);
                }
            });
        },
        selectAllResponseCodes() {
            this.$nextTick(() => {
                if (this.selectedAllResponseCodes) {
                    this.response_codes = [];
                } else {
                    const resp = [...this.companyResponseCodes];
                    this.response_codes = resp.map(item => item.response_code);
                }
            });
        },
        selectAllBillingCodes() {
            this.$nextTick(() => {
                if (this.selectedAllBillingCodes) {
                    this.billing_codes = [];
                } else {
                    const billing = [...this.companyBillingCodes];
                    this.billing_codes = billing.map(item => item.code);
                }
            });
        },
        validateForm() {
            this.formValid = this.requiredParams !== '' ? this.requiredParams.every(param => param && param.length > 0) : true;                   
        },
        selectDate(value) {
            if (value.length !== 2) {
                this.showAlert = true;
                this.alertMessage = 'Please select date range';
                return;
            }
            this.$refs.dateMenu.save(value);
        },
        async generateReport() {
            this.cid = this.user.cid;
            
            if(this.report.param_csv.includes('date_from')) {
                const dateRange = this.dateText.split(' ~ ');
                this.date_from = dateRange[0];
                this.date_to = dateRange[1];
            }
            // loop through report.param_csv and build param object
            let params = this.report.param_csv.split(',');
            let param = {};
            params.forEach((item) => {
                param[item] = this[item];
            });

            param['order-by'] = this.orderBy,
            param.format= this.format;
           
            this.reportGenerateStatus = 'PROCESSING';
            const response = await webtmsBackend.generateReportTMS(param, this.report.name);
            if (response.task && response.task.rowid) {
                this.reportRowId = response.task.rowid;
                this.checkReportStatus();
            } else {
                this.reportGenerateError = response.err || 'Failed to generate report';
                this.reportGenerateStatus = 'FAILED';
            }
        },
        async checkReportStatus() {
            const response = await webtmsBackend.checkReportStatus(this.reportRowId, this.report.name);
            if (response.task && response.task.processed === true) {
                if (response.task.success === true) {
                    this.reportGenerateStatus = 'PROCESSED';
                    this.reportFileName = response.task.task_output_ref;
                } else {
                    this.reportGenerateStatus = 'FAILED';
                    this.reportGenerateError = response.task.task_running_status;
                }
            }
            // only if we got back a valid task and !processed, check again in 2 seconds
            if (response.task && response.task.processed === false) {
                setTimeout(() => {
                    this.checkReportStatus();
                }, 2000);
            }
        },
        // Select All Locators
        selectAllLocators() {
            this.$nextTick(() => {
                if (this.selectedAllLocators) {
                    this.locators = [];
                } else {
                    const options = [...this.companyLocators];
                    this.locators = options.map(item => item.uid);
                }
            });
        },
        closeDialog() {
            this.show = false;
            this.resetFieldsToDefault();            
        }
    }
};
</script>
<style scoped>
    .download-file {
    background-color: #F5F7FA;
    min-height: 55px;
    padding: 0 5px;
    }
    .file-icon {
    font-size: 30px;
    }
</style>
